import * as React from 'react';
import { useTysonCoaching } from '../contexts/TysonCoachingContext';
import { TysonCoachingContextData } from '../types/coaching';

interface UseRealCallCoachingSessionProps {
	companyId: string | null;
	contactId: string | null;
	leadServedId?: string;
	phoneCallId?: string;
}

interface UseRealCallCoachingSessionReturn {
	startCoachingSession: () => Promise<boolean>;
	stopCoachingSession: () => void;
	requestEndOfCallSummary: () => void;
	isStarting: boolean;
	isReady: boolean;
	error: string | null;
}

/**
 * Hook for managing the coaching session lifecycle for real Twilio calls.
 * Handles starting coaching BEFORE the call and cleanup after.
 */
export const useRealCallCoachingSession = ({
	companyId,
	contactId,
	leadServedId,
	phoneCallId,
}: UseRealCallCoachingSessionProps): UseRealCallCoachingSessionReturn => {
	const coaching = useTysonCoaching();
	const [isStarting, setIsStarting] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	// Track if coaching is ready (has conversationId and not responding)
	const isReady = coaching.isActive && !!coaching.conversationId && !coaching.isCreatingConversation;

	const startCoachingSession = React.useCallback(async (): Promise<boolean> => {
		if (!companyId || !contactId) {
			setError('Missing company or contact ID');
			return false;
		}

		if (coaching.isActive) {
			// Already active, consider it ready
			return true;
		}

		setIsStarting(true);
		setError(null);

		try {
			const contextData: TysonCoachingContextData = {
				companyId,
				contactId,
				leadServedId,
				phoneCallId,
			};

			await coaching.startCoaching(contextData);

			// Wait for conversation to be ready (max 30 seconds)
			let attempts = 0;
			const maxAttempts = 30;

			while (attempts < maxAttempts) {
				await new Promise(resolve => setTimeout(resolve, 1000));
				attempts++;

				// Check if we have a conversation ID and AI has responded
				if (coaching.conversationId && !coaching.isAIResponding && !coaching.isCreatingConversation) {
					setIsStarting(false);
					return true;
				}
			}

			setError('Timeout waiting for coaching to initialize');
			setIsStarting(false);
			return false;
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to start coaching');
			setIsStarting(false);
			return false;
		}
	}, [companyId, contactId, leadServedId, phoneCallId, coaching]);

	const stopCoachingSession = React.useCallback(() => {
		coaching.stopCoaching();
		setError(null);
	}, [coaching]);

	const requestEndOfCallSummary = React.useCallback(() => {
		if (coaching.isActive && coaching.conversationId) {
			coaching.sendEndOfCallSummary();
		}
	}, [coaching]);

	return {
		startCoachingSession,
		stopCoachingSession,
		requestEndOfCallSummary,
		isStarting,
		isReady,
		error,
	};
};
